import { Routes, Route, Navigate } from "react-router-dom";
import Shell from "../../components/Shell";
import { useAuth } from "../../context/AuthContext";
import useContactRequestsUnread from "../../lib/useContactRequestsUnread";
import useApprovalsCount from "../../lib/useApprovalsCount";
import AdminDashboard from "./AdminDashboard";
import AdminDashboardV2 from "./AdminDashboardV2";
import AdminCalendar from "./AdminCalendar";
import AdminDaySheet from "./AdminDaySheet";
import AdminManualCleanings from "./AdminManualCleanings";
import AdminCapacity from "./AdminCapacity";
import AdminUsers from "./AdminUsers";
import AdminBookings from "./AdminBookings";
import AdminNewBooking from "./AdminNewBooking";
import AdminFacturalusa from "./AdminFacturalusa";
import AdminExtras from "./AdminExtras";
import AdminPromotion from "./AdminPromotion";
import AdminApartments from "./AdminApartments";
import AdminMap from "./AdminMap";
import AdminLeaderboard from "./AdminLeaderboard";
import AdminIssues from "./AdminIssues";
import AdminMaintenance from "./AdminMaintenance";
import AdminExtrasSuggestions from "./AdminExtrasSuggestions";
import AdminPricing from "./AdminPricing";
import AdminFinance from "./AdminFinance";
import AdminExpenses from "./AdminExpenses";
import AdminStockInvoices from "./AdminStockInvoices";
import AdminGallery from "./AdminGallery";
import AdminReferrals from "./AdminReferrals";
import AdminTutorials from "./AdminTutorials";
import AdminEmails from "./AdminEmails";
import AdminEmailsStudio from "./AdminEmailsStudio";
import AdminAnalytics from "./AdminAnalytics";
import AdminContactRequests from "./AdminContactRequests";
import AdminRescheduleRequests from "./AdminRescheduleRequests";
import AdminFinancialCharts from "./AdminFinancialCharts";
import AdminEmailAudit from "./AdminEmailAudit";
import AdminRadio from "./AdminRadio";
import AdminTestimonials from "./AdminTestimonials";
import AdminTeam from "./AdminTeam";
import AdminNps from "./AdminNps";
import AdminHeatmap from "./AdminHeatmap";
import AdminPaymentProofs from "./AdminPaymentProofs";
import AdminContracts from "./AdminContracts";
import AdminProfile from "./AdminProfile";
import AdminCompany from "./AdminCompany";
import SupervisionReportsList from "../reports/SupervisionReportsList";
import SupervisionReportDetail from "../reports/SupervisionReportDetail";
import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import useGalleryEnabled from "../../lib/useGalleryEnabled";

function buildNav(unreadContacts, pendingTestimonials, pendingIssues, pendingExtras, pendingProofs, galleryEnabled) {
  const items = [
    { key: "dashboard", path: "/admin", label: "Dashboard" },
    { key: "calendar", path: "/admin/agenda", label: "Agenda" },
    { key: "capacity", path: "/admin/capacidade", label: "Capacidade" },
    { key: "map", path: "/admin/mapa", label: "Mapa" },
    { key: "bookings", path: "/admin/reservas", label: "Reservas" },
    { key: "reports", path: "/admin/relatorios", label: "Relatórios" },
    { key: "contracts", path: "/admin/contratos", label: "Contratos" },
    { key: "payment-proofs", path: "/admin/comprovantes", label: "Comprovantes", badge: pendingProofs },
    { key: "reschedule", path: "/admin/alteracoes", label: "Alterações" },
    { key: "apartments", path: "/admin/apartamentos", label: "Apartamentos" },
    { key: "users", path: "/admin/utilizadores", label: "Utilizadores" },
    { key: "leaderboard", path: "/admin/ranking", label: "Ranking" },
    { key: "issues", path: "/admin/problemas", label: "Problemas", badge: pendingIssues },
    { key: "maintenance", path: "/admin/manutencoes", label: "Manutenções" },
    { key: "extras", path: "/admin/extras-sugeridos", label: "Extras sugeridos", badge: pendingExtras },
    { key: "pricing", path: "/admin/precos", label: "Preços" },
    { key: "finance", path: "/admin/financas", label: "Finanças" },
    { key: "finance-charts", path: "/admin/graficos-financeiros", label: "Gráficos financeiros" },
    { key: "email-audit", path: "/admin/emails", label: "Emails enviados" },
    { key: "emails-studio", path: "/admin/comunicacoes", label: "Comunicações" },
    { key: "radio", path: "/admin/radio", label: "Rádio" },
    { key: "products", path: "/admin/produtos", label: "Produtos" },
    { key: "materials", path: "/admin/materiais", label: "Materiais" },
    { key: "stock-invoices", path: "/admin/faturas-stock", label: "Stock de produtos" },
    { key: "expenses", path: "/admin/despesas", label: "Despesas" },
    { key: "manual-cleanings", path: "/admin/limpezas-extra", label: "Limpezas extra" },
    { key: "invoices", path: "/admin/faturas", label: "Faturação" },
    { key: "extras", path: "/admin/extras", label: "Extras (preços)" },
    { key: "promotion", path: "/admin/promocao", label: "Promoção" },
    { key: "referrals", path: "/admin/indicacoes", label: "Indicações" },
    { key: "tutorials", path: "/admin/tutoriais", label: "Tutoriais" },
    { key: "contacts", path: "/admin/contactos", label: "Pedidos de contacto", badge: unreadContacts },
    { key: "testimonials", path: "/admin/testemunhos", label: "Testemunhos", badge: pendingTestimonials },
    { key: "team", path: "/admin/equipa", label: "Equipa da semana" },
    { key: "nps", path: "/admin/nps", label: "NPS" },
    { key: "heatmap", path: "/admin/mapa-calor", label: "Heat-map" },
    { key: "emails-sistema", path: "/admin/emails-sistema", label: "Emails (sistema)" },
    { key: "analytics", path: "/admin/analytics", label: "Analytics" },
    { key: "admin-gallery", path: "/admin/galeria", label: "Galeria (editar)" },
    { key: "company", path: "/admin/empresa", label: "Dados da Empresa" },
    { key: "profile", path: "/admin/perfil", label: "O meu perfil" },
  ];
  // "Ver galeria pública" only makes sense when the gallery is on. Hide it
  // (for everyone, including admin) when the admin has disabled the gallery.
  if (galleryEnabled) {
    items.push({ key: "gallery", path: "/galeria", label: "Ver galeria pública" });
  }
  return items;
}

export default function AdminApp() {
  const { user } = useAuth() || {};
  const unread = useContactRequestsUnread(user?.role);
  const approvals = useApprovalsCount(user?.role);
  const [pendingTestimonials, setPendingTestimonials] = useState(0);
  const [pendingProofs, setPendingProofs] = useState(0);

  useEffect(() => {
    if (user?.role !== "admin") return;
    let cancelled = false;
    async function load() {
      try {
        const { data } = await api.get("/admin/testimonials?status=pending");
        if (!cancelled) setPendingTestimonials(data.pending_count || 0);
      } catch (_) { /* silent */ }
      try {
        const { data: pd } = await api.get("/admin/bookings/payment-proofs/pending");
        if (!cancelled) setPendingProofs(pd.count || 0);
      } catch (_) { /* silent */ }
    }
    load();
    const id = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, [user?.role]);

  const galleryEnabled = useGalleryEnabled();
  const NAV = buildNav(unread, pendingTestimonials, approvals.pending_issues, approvals.pending_extras, pendingProofs, galleryEnabled);
  return (
    <Shell title="Área Admin" nav={NAV} testIdPrefix="admin">
      <Routes>
        <Route index element={<AdminDashboardV2 />} />
        <Route path="dashboard-classico" element={<AdminDashboard />} />
        <Route path="calendario" element={<AdminCalendar />} />
        <Route path="agenda" element={<AdminCalendar />} />
        <Route path="folha-do-dia/:date" element={<AdminDaySheet />} />
        <Route path="limpezas-extra" element={<AdminManualCleanings />} />
        <Route path="capacidade" element={<AdminCapacity />} />
        <Route path="mapa" element={<AdminMap />} />
        <Route path="reservas" element={<AdminBookings />} />
        <Route path="reservas/nova" element={<AdminNewBooking />} />
        <Route path="relatorios" element={<SupervisionReportsList basePath="/admin/relatorios" />} />
        <Route path="relatorios/:id" element={<SupervisionReportDetail basePath="/admin/relatorios" />} />
        <Route path="contratos" element={<AdminContracts />} />
        <Route path="comprovantes" element={<AdminPaymentProofs />} />
        <Route path="alteracoes" element={<AdminRescheduleRequests />} />
        <Route path="apartamentos" element={<AdminApartments />} />
        <Route path="utilizadores" element={<AdminUsers />} />
        <Route path="ranking" element={<AdminLeaderboard />} />
        <Route path="problemas" element={<AdminIssues />} />
        <Route path="manutencoes" element={<AdminMaintenance />} />
        <Route path="extras-sugeridos" element={<AdminExtrasSuggestions />} />
        <Route path="precos" element={<AdminPricing />} />
        <Route path="financas" element={<AdminFinance />} />
        <Route path="graficos-financeiros" element={<AdminFinancialCharts />} />
        <Route path="emails" element={<AdminEmailAudit />} />
        <Route path="comunicacoes" element={<AdminEmailsStudio />} />
        <Route path="radio" element={<AdminRadio />} />
        <Route path="produtos" element={<AdminExpenses mode="produto" />} />
        <Route path="materiais" element={<AdminExpenses mode="material" />} />
        <Route path="faturas-stock" element={<AdminStockInvoices />} />
        <Route path="despesas" element={<AdminExpenses mode="gasto" />} />
        <Route path="produtos-gastos" element={<Navigate to="/admin/despesas" replace />} />
        <Route path="faturas" element={<AdminFacturalusa />} />
        <Route path="extras" element={<AdminExtras />} />
        <Route path="promocao" element={<AdminPromotion />} />
        <Route path="indicacoes" element={<AdminReferrals />} />
        <Route path="tutoriais" element={<AdminTutorials />} />
        <Route path="contactos" element={<AdminContactRequests />} />
        <Route path="testemunhos" element={<AdminTestimonials />} />
        <Route path="equipa" element={<AdminTeam />} />
        <Route path="nps" element={<AdminNps />} />
        <Route path="mapa-calor" element={<AdminHeatmap />} />
        <Route path="emails-sistema" element={<AdminEmails />} />
        <Route path="analytics" element={<AdminAnalytics />} />
        <Route path="galeria" element={<AdminGallery />} />
        <Route path="perfil" element={<AdminProfile />} />
        <Route path="empresa" element={<AdminCompany />} />
        <Route path="*" element={<Navigate to="/admin" replace />} />
      </Routes>
    </Shell>
  );
}
